
import Vue from 'vue';
import { Data as ArticleData } from '@root/modules/article/types/article';
import { Data as HeadlinesData, DataWithHref } from '@root/modules/headlines/types/headlines';
import UniversalHeadlinesService from '@root/modules/headlines/services/UniversalHeadlines.service';
import getHeadlineWithHref from '@root/modules/headlines/utils/getHeadlineWithHref';
import HeadlineBlocks from '@root/common/components/base/headline/HeadlineBlocks.vue';

interface Props {
  article: ArticleData;
}

interface Methods {
  getHeadline: (headline: HeadlinesData) => DataWithHref;
  getSimilarHeadlines: () => void;
  getHeadlines: () => Promise<HeadlinesData[]>;
}

interface Data {
  data: Record<string, any>;
  domain: string;
  headlines: HeadlinesData[];
  limit: number;
  settings: Record<string, any>;
}

interface DefaultWindow extends Window {
  videoPlaylist: string[] | undefined;
}

declare const window: DefaultWindow;

export default Vue.extend<Data, Methods, unknown, Props>({
  components: {
    HeadlineBlocks,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
  },
  data() {
    const { domain } = this.$channelConfig('settings');
    return {
      data: {
        items: [],
      },
      domain,
      headlines: [],
      limit: 5,
      settings: {
        category: false,
        channel: false,
        lead: false,
        publishDate: false,
        author: false,
        titleComment: true,
      },
    };
  },
  watch: {
    headlines() {
      // Export videoPlaylist with similar headlines URLs for player
      const videoPlaylist = this.headlines.map((headline: any) => `https://www.delfi.lt/a/${headline.id}`);
      window.videoPlaylist = videoPlaylist;

      // Store current item localStorage to prevent looping
      const videoPlaylistWatchedString = window.localStorage.getItem('videoPlaylistWatched') || null;
      let videoPlaylistWatched: string[] = [];
      if (videoPlaylistWatchedString) {
        videoPlaylistWatched = JSON.parse(videoPlaylistWatchedString);
      }
      videoPlaylistWatched.push(`https://www.delfi.lt/a/${this.article.id}`);
      window.localStorage.setItem('videoPlaylistWatched', JSON.stringify(videoPlaylistWatched));
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.getSimilarHeadlines();
    });
  },
  destroyed() {
    window.videoPlaylist = undefined;
  },
  methods: {
    getHeadline(headline) {
      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
      return getHeadlineWithHref({ headline, domain: this.domain, fakeDomainEnabled });
    },
    async getSimilarHeadlines() {
      const headlines = await this.getHeadlines();
      if (headlines.length) {
        this.headlines = headlines.map((headline) => this.getHeadline(headline)) || [];
      }
    },
    async getHeadlines() {
      const excludeIds = [this.$props.article.id];
      const variables: any = {
        domain: this.domain,
        excludeIds,
        limit: this.limit,
      };

      const fallbackVariables: any = {
        domain: this.domain,
        categoryId: [this.$props.article.primaryCategory.parentCategory?.id],
        excludeIds,
        limit: this.limit,
        lite: true,
      };

      if (this.$props.article.tags.items.length && this.$props.article.primaryCategory.parentCategory?.id !== 94378737) {
        variables.tagId = this.$props.article.tags.items.map((t: any) => t.id);
      } else {
        variables.categoryId = [this.$props.article.primaryCategory.id];
      }

      const universalHeadlinesService = new UniversalHeadlinesService();
      let headlines = await universalHeadlinesService.fetch(variables);

      // Fetch fallback query if primary has zero results
      if (!headlines.items.length && fallbackVariables) {
        headlines = await universalHeadlinesService.fetch(fallbackVariables);
      }

      return headlines.items;
    },
  },
});
